/* global G */
import settings from '@tenant/settings'
import { curry, type PlatformEvent, setKey } from 'lib/util'
import { setData } from 'lib/sequence/model/api/set'
import { actionWithHooks } from 'lib/sequence/module/action'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import { GET_INFO } from 'app/xEcoApm/helper/apm'
import { persistSensor } from 'app/xEcoApm/helper/offline'

const { bluetooth }: { bluetooth: any } = settings

/**
 * Displays a dialog asking the user to confirm the pairing
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {Promise<*>}
 */
const showConfirmDialog = async (module: Gaia.AppModule.Spec): Promise<any> => {
  return await showBlockingDialog(module, null, {
    title: {
      ns: 'apmGo',
      key: 'dialog.pairDevice.title',
      defaultValue: 'Connect',
    },
    text: {
      ns: 'apmGo',
      key: 'dialog.pairDevice.text',
      defaultValue: 'Do you want to connect to the peripheral?',
    },
    ok: {
      ns: 'common',
      key: 'button.ok',
      defaultValue: 'Ok',
    },
    cancel: {
      ns: 'common',
      key: 'button.cancel',
      defaultValue: 'Cancel',
    },
  })
}

/**
 * Displays a dialog informing the user that a device could not be paired and asking them whether
 * they want to retry.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {Promise<*>}
 */
const showErrorDialog = async (module: Gaia.AppModule.Spec): Promise<any> => {
  return await showBlockingDialog(module, null, {
    title: {
      ns: 'apmGo',
      key: 'dialog.pairDeviceError.title',
      defaultValue: 'Device could not be paired',
    },
    text: {
      ns: 'apmGo',
      key: 'dialog.pairDeviceError.text',
      defaultValue: 'Please check that the device is turned on and within range, then try again.',
    },
    ok: {
      ns: 'common',
      key: 'button.retry',
      defaultValue: 'Retry',
    },
    cancel: {
      ns: 'common',
      key: 'button.cancel',
      defaultValue: 'Cancel',
    },
  })
}

/**
 * Attempts to obtain information about a connected peripheral, identified by `id`.
 *
 * @param module        current module
 * @param pairedDevice  peripheral's connection information
 */
const getDeviceInfo = async (module: Gaia.AppModule.Spec, pairedDevice: any) => {
  const bluetoothAdapter = module[G.ADAPTER][G.BLUETOOTH]
  const peripheralName = pairedDevice.name || pairedDevice.value?.name
  const peripheral = bluetoothAdapter[G.GROUP][peripheralName]
  let info: { d: string, n: string, f: number, h: number } | null = null
  let retries = 0

  while(info === null) {
    try {
      const { d, n, f, h } = await peripheral[G.API].send(GET_INFO)
      info = { d, n, f, h }
    } catch (e) {
      console.warn(e)
      if (retries++ >= 6) {
        throw e
      }
    }
  }

  const { d, n, f, h } = info!

  return {
    deviceId: d,
    serial: n,
    firmwareVersion: String(f),
    hardwareVersion: String(h),
  }
}

/**
 * Attempts to pair a peripheral by its `id`.
 *
 * @param module  current module
 * @param event   event object containing the peripheral's id
 */
const pair = async (
  module: Gaia.AppModule.Spec,
  event: PlatformEvent,
): Promise<any> => await new Promise((resolve, reject) => {
  const bluetoothAdapter = module[G.ADAPTER][G.BLUETOOTH]

  const connect = async () => {
    while(true) {
      try {
        const peripheral = await bluetoothAdapter[G.API].connect({
          detail: {
            [G.DATA]: {
              ...event?.detail || event || {},
              service: bluetooth?.constants.apm.service,
              characteristics: bluetooth?.constants.apm.characteristics,
            },
          },
        })
        resolve(peripheral?.item)
        break
      } catch (e) {
        if (!await showErrorDialog(module)) {
          throw e
        }
      }
    }
  }

  connect().catch(reject)
})

/**
 * Pair Device Event Handler
 *
 * Tries to pair with the incoming device.
 *
 * @param module     the current module composition object
 * @param component  the current action's main component
 * @param event      information about the event that triggered this handler
 */
const pairDevice = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec | null,
  event: PlatformEvent
): Promise<void> => {
  const online = module[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]

  if (!await showConfirmDialog(module)) {
    return
  }

  const pairedDevice = await pair(module, event)
  const pairedDeviceInfo = await getDeviceInfo(module, pairedDevice)

  const { pairing, sensor } = module[G.MODEL][G.CHILDREN]

  setData(pairing, {
    ...pairedDeviceInfo,
    name: pairedDevice.value.name,
    licensedUntil: 'Unlicensed',
    licenceUpload: false,
  }, { updateCache: true })

  if (online) {
    await actionWithHooks(module[G.ACTIONS].pairingSubmit)([])
  } else {
    const sensorId = await persistSensor(module)
    // setting sensor key for `redirectWithModelKey`
    setKey(sensorId, G.REF, sensor[G.STATE])
  }
}

export default curry(pairDevice)
